import React, { useState, useEffect, useCallback } from 'react';
import { translations, Language } from '../translations';
import { ApiService } from '../services/api';
import SalesChat from './SalesChat';
import { SubscriptionPlan, LandingPageContent, HeroLabel, SiteAssets, PricingConfig, TutorialVideo } from '../types';
import { EditableText } from './EditableText';
import { IconPicker } from './IconPicker';
import VideoTutorialsSlice from './VideoTutorialsSlice';
import VideoPlayerModal from './VideoPlayerModal';
import AmbassadorSlice from './AmbassadorSlice';
import MediaGalleryModal from './MediaGalleryModal';
import EditableImage from './EditableImage';
import { AlertTriangle, BarChart, Bot, Calendar, Camera, Coins, Flame, Image, Map, MapPin, Radio, Ruler, Scale, Shield, ShoppingCart, Sparkles, Trophy, Users, Wind, Play } from 'lucide-react';
import * as LucideIcons from 'lucide-react';

const renderLucideIcon = (iconStr: any) => {
    if (!iconStr) return null;
    if (typeof iconStr !== 'string') return iconStr;
    const s = iconStr.trim();
    if (s.includes('Map className') || s.includes('🗺️')) return <Map className="w-[1em] h-[1em] inline mr-1 text-emerald-400" />;
    if (s.includes('MapPin') || s.includes('📍')) return <MapPin className="w-[1em] h-[1em] inline mr-1 text-rose-400" />;
    if (s.includes('Flame') || s.includes('🔥')) return <Flame className="w-[1em] h-[1em] inline mr-1 text-amber-500" />;
    if (s.includes('Sparkles') || s.includes('✨')) return <Sparkles className="w-[1em] h-[1em] inline mr-1 text-purple-400" />;
    if (s.includes('Ruler') || s.includes('📐')) return <Ruler className="w-[1em] h-[1em] inline mr-1 text-yellow-400" />;
    if (s.includes('Bot') || s.includes('🤖')) return <Bot className="w-[1em] h-[1em] inline mr-1 text-indigo-400" />;
    if (s.includes('Users') || s.includes('👥') || s.includes('🧑‍🤝‍🧑') || s.includes('👨‍👩‍👦') || s.includes('👨‍👨‍👦') || s.includes('👨‍👩‍👧')) return <Users className="w-[1em] h-[1em] inline mr-1 text-orange-400" />;
    if (s.includes('BarChart') || s.includes('📊')) return <BarChart className="w-[1em] h-[1em] inline mr-1 text-teal-400" />;
    if (s.includes('Scale') || s.includes('⚖️')) return <Scale className="w-[1em] h-[1em] inline mr-1 text-pink-400" />;
    if (s.includes('Shield') || s.includes('🛡️')) return <Shield className="w-[1em] h-[1em] inline mr-1 text-sky-500" />;
    if (s.includes('Wind') || s.includes('💨')) return <Wind className="w-[1em] h-[1em] inline mr-1 text-cyan-400" />;
    if (s.includes('Radio') || s.includes('📡')) return <Radio className="w-[1em] h-[1em] inline mr-1 text-red-500" />;
    if (s.includes('Trophy') || s.includes('🏆')) return <Trophy className="w-[1em] h-[1em] inline mr-1 text-yellow-500" />;
    if (s.includes('ShoppingCart') || s.includes('🛒')) return <ShoppingCart className="w-[1em] h-[1em] inline mr-1 text-emerald-500" />;
    if (s.includes('Calendar') || s.includes('📅') || s.includes('🗓️')) return <Calendar className="w-[1em] h-[1em] inline mr-1 text-sky-400" />;
    if (s.includes('AlertTriangle') || s.includes('⚠️')) return <AlertTriangle className="w-[1em] h-[1em] inline mr-1 text-amber-500" />;
    
    // Fallback: Dynamically resolve any other Lucide Icon name
    let cleanName = s;
    if (s.includes('className')) {
        const match = s.match(/<([A-Za-z0-9]+)/);
        if (match) cleanName = match[1];
    }
    const IconComponent = (LucideIcons as any)[cleanName];
    if (IconComponent) {
        return <IconComponent className="w-[1em] h-[1em] inline mr-1" />;
    }

    return <span dangerouslySetInnerHTML={{ __html: s }} />;
};

interface LandingPageProps {
    onEnterApp: (intent?: 'login' | 'register' | 'buy') => void;
    lang: Language;
    setLang: (l: Language) => void;
    editable?: boolean;
    country?: string;
}

const DEFAULT_CONTENT: LandingPageContent = {
    hero: {
        title: 'Plane smarter.\nFliege weiter.',
        subtitle: 'Der KI-Copilot für Gleitschirmpiloten',
        description: 'Deine professionelle Flugplanungs-Suite für die Schweiz, Deutschland und Österreich. Mit KI-gestützter Routenanalyse, Thermik-Karten und einem digitalen Mentor.',
        ctaPrimary: 'Kostenlos registrieren',
        ctaSecondary: 'Gleich Abo kaufen',
        labels: [
            { id: 'l1', icon: '<Shield className="w-4 h-4 inline mr-1" />', title: 'Luftraum Check', value: 'CTR Aktiv', x: 20, y: 30, color: 'red' },
            { id: 'l2', icon: '<Wind className="w-4 h-4 inline mr-1" />', title: 'Wind Analyse', value: '12 km/h NW', x: 80, y: 15, color: 'emerald' },
            { id: 'l3', icon: '<Radio className="w-4 h-4 inline mr-1" />', title: 'Live Tracking', value: 'Online', x: 10, y: 70, color: 'indigo' }
        ]
    },
    featuresSection: {
        title: 'Warum Streckenfliegen.app?',
        subtitle: 'Die zentrale Plattform für den gesamten Alpenraum (CH/DE/AT).'
    },
    features: [
        { icon: '<Map className="w-4 h-4 inline mr-1" />', title: 'Detaillierte Karten', description: 'Hochpräzise Karten für die gesamte DACH-Region mit integrierten Thermik-Hotspots.', badges: ['FREE', 'XC PRO'] },
        { icon: '<Bot className="w-4 h-4 inline mr-1" />', title: 'Dein persönlicher KI Copilot', description: 'Dein persönlicher Assistent für Routenplanung, Wetteranalyse und Taktik.', badges: ['XC PRO'] },
        { icon: '<BarChart className="w-4 h-4 inline mr-1" />', title: 'KI Basierte Fluganalyse', description: 'Lade deine IGC-Tracks hoch. Die KI analysiert deine Fehler und lobt deine Erfolge.', badges: ['XC PRO'] }
    ], // Will be expanded to 12
    team: {
        title: 'Von Piloten\nFür Piloten',
        subtitle: 'Made in the Alps',
        text: [
            'Streckenfliegen.app wurde mit einer einfachen Mission gegründet: Die Komplexität der Flugplanung zu reduzieren, damit du dich auf das konzentrieren kannst, was wirklich zählt – das Fliegen.',
            'Unser Team besteht aus erfahrenen XC-Piloten, Software-Ingenieuren und Datenanalysten aus der Schweiz und Deutschland. Wir kombinieren unsere Leidenschaft für das Paragliding mit modernster KI-Technologie.'
        ],
        badges: ['100% Swiss Quality Code', 'Datenschutz nach DSGVO', 'Ständige Weiterentwicklung']
    },
    trophy: {
        title: 'Streckenfliegen.app\nTrophy',
        subtitle: 'Community Giveback',
        description: 'Wir glauben an die Förderung des Sports. Deshalb fliessen 10% aller Einnahmen direkt zurück an die besten Piloten der Community.',
        percentage: '10%',
        placement: 'Prämiert werden die Ränge 1-3 im XContest für die Klassen A, B, C und D.'
    },
    footer: {
        title: 'Streckenfliegen.app',
        description: 'Die Nr. 1 Plattform für Streckenpiloten in der Schweiz, Deutschland und Österreich.'
    }
};

const LandingPage: React.FC<LandingPageProps> = ({ onEnterApp, lang, setLang, editable = false, country: countryProp }) => {
    const [scrolled, setScrolled] = useState(false);

    // Order State
    const defaultOrder = ['hero', 'ambassador', 'features', 'tutorials', 'trophy', 'pricing', 'about'];
    const [sliceOrder, setSliceOrder] = useState<string[]>(defaultOrder);

    const [mediaModalOpen, setMediaModalOpen] = useState(false);
    const [mediaTarget, setMediaTarget] = useState<string | null>(null);

    const handleMediaSelect = async (url: string) => {
        if (mediaTarget?.startsWith('sliceBg:')) {
            const sliceId = mediaTarget.split(':')[1];
            const newContent = { ...content };
            if (!newContent.sliceBackgrounds) newContent.sliceBackgrounds = {};
            newContent.sliceBackgrounds[sliceId] = { ...newContent.sliceBackgrounds[sliceId], bgUrl: url };
            setContent(newContent);
            await ApiService.saveSystemSetting('landing_page_content', JSON.stringify(newContent));
            if (editable) window.dispatchEvent(new CustomEvent('landing-page-update', { detail: newContent }));
            return;
        }

        if (mediaTarget === 'heroAvatarUrl') {
            if (url !== heroAvatarUrl) {
                setHeroAvatarUrl(url);
                try {
                    const settings = await ApiService.getSystemSettings();
                    const currentAssets = settings?.['site_assets'] ? JSON.parse(settings['site_assets']) : {};
                    const newAssets = { ...currentAssets, heroAvatarUrl: url };
                    await ApiService.saveSystemSetting('site_assets', JSON.stringify(newAssets));
                    window.dispatchEvent(new CustomEvent('site-assets-update', { detail: newAssets }));
                } catch (err) { console.error("Asset save fail:", err); }
            }
            return;
        }

        let currentUrl = mediaTarget === 'heroBgUrl' ? heroBgUrl : heroUrl;
        if (url !== currentUrl) {
            if (mediaTarget === 'heroBgUrl') {
                setHeroBgUrl(url);
            } else {
                setHeroUrl(url);
            }
            try {
                const settings = await ApiService.getSystemSettings();
                const currentAssets = settings?.['site_assets'] ? JSON.parse(settings['site_assets']) : {};
                const newAssets = { ...currentAssets, [mediaTarget === 'heroBgUrl' ? 'heroBgUrl' : 'heroUrl']: url };
                await ApiService.saveSystemSetting('site_assets', JSON.stringify(newAssets));
                window.dispatchEvent(new CustomEvent('site-assets-update', { detail: newAssets }));
                if (editable) window.dispatchEvent(new CustomEvent('landing-page-update', { detail: content }));
            } catch (err) {
                console.error("Asset save fail:", err);
            }
        }
    };


    useEffect(() => {
        const loadOrder = async () => {
            try {
                const settings = await ApiService.getSystemSettings();
                if (settings && settings['landing_page_order']) {
                    setSliceOrder(JSON.parse(settings['landing_page_order']));
                }
            } catch {}
        };
        loadOrder();
    }, []);

    const moveSlice = async (idx: number, dir: number) => {
        if (!editable) return;
        const newIdx = idx + dir;
        if (newIdx < 0 || newIdx >= sliceOrder.length) return;
        
        const newOrder = [...sliceOrder];
        const temp = newOrder[idx];
        newOrder[idx] = newOrder[newIdx];
        newOrder[newIdx] = temp;
        
        setSliceOrder(newOrder);
        try {
            await ApiService.saveSystemSetting('landing_page_order', JSON.stringify(newOrder));
        } catch (e) {
            console.error("Save order fail", e);
        }
    };

    const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
    const [loadingPlans, setLoadingPlans] = useState(true);
    const [activeLegal, setActiveLegal] = useState<'impressum' | 'privacy' | 'terms' | 'datasources' | null>(null);
    const [draggedLabel, setDraggedLabel] = useState<string | null>(null);
    const [draggedFeatureIndex, setDraggedFeatureIndex] = useState<number | null>(null);
    const [iconPickerTarget, setIconPickerTarget] = useState<{ type: 'label' | 'feature' | 'badge', index: number } | null>(null);
    const [activeColorWord, setActiveColorWord] = useState<string | null>(null);
    const [hexInput, setHexInput] = useState('');
    const [geoCountry, setGeoCountry] = useState<string>(countryProp || 'CH');
    const [pricingConfig, setPricingConfig] = useState<PricingConfig>({
        vat_rate_ch: 8.1, vat_rate_de: 19, vat_rate_at: 20, exchange_rate_eur_chf: 0.93
    });

    // Tutorial Videos
    const [tutorials, setTutorials] = useState<TutorialVideo[]>([]);
    const [playingVideo, setPlayingVideo] = useState<TutorialVideo | null>(null);

    // Content State
    const [content, setContent] = useState<LandingPageContent>(DEFAULT_CONTENT);
    const [heroBgUrl, setHeroBgUrl] = useState('');
    const [heroUrl, setHeroUrl] = useState('');
    const [heroScale, setHeroScale] = useState(1);
    const [heroPos, setHeroPos] = useState({ x: 50, y: 50 });
    const [teamPhotoUrl, setTeamPhotoUrl] = useState('');
    const [teamPhotoScale, setTeamPhotoScale] = useState(1);
    const [teamPhotoPos, setTeamPhotoPos] = useState({ x: 50, y: 50 });
    const [heroAvatarUrl, setHeroAvatarUrl] = useState('/cmPortraitIQ.jpg');

    const [bgEditorOpenFor, setBgEditorOpenFor] = useState<string | null>(null);

    const updateSliceBg = (sliceId: string, updates: any) => {
        const newContent = { ...content };
        if (!newContent.sliceBackgrounds) newContent.sliceBackgrounds = {};
        newContent.sliceBackgrounds[sliceId] = { ...(newContent.sliceBackgrounds[sliceId] || {}), ...updates };
        handleContentUpdate(newContent);
        if (editable) window.dispatchEvent(new CustomEvent('landing-page-update', { detail: newContent }));
    };

    // Sync country prop
    useEffect(() => { if (countryProp) setGeoCountry(countryProp); }, [countryProp]);

    // Load Content & Plans
    useEffect(() => {
        const load = async () => {
            try {
                // 0. Pricing Config & Geo Detection
                ApiService.getPricingConfig().then(c => { if (c) setPricingConfig(c); }).catch(() => {});
                if (!countryProp && !editable) {
                    fetch('/api/geo/country').then(r => r.json()).then(d => {
                        if (d.code) setGeoCountry(d.code);
                    }).catch(() => {});
                }

                // 1. Plans & Tutorials
                const [plansData, tutsRes] = await Promise.all([
                    ApiService.getSubscriptionPlans().catch(e => []),
                    ApiService.apiRequest('/tutorials/all').catch(() => null)
                ]);
                setPlans(plansData.sort((a: any, b: any) => (a.sort_order || 0) - (b.sort_order || 0) || a.price - b.price));
                if (tutsRes && tutsRes.ok) {
                    setTutorials(await tutsRes.json());
                }

                // 2. Content - load country-specific if available
                const activeCountry = countryProp || geoCountry;
                let parsed: any = null;

                // Try country-specific content first (for public page)
                if (!editable) {
                    try {
                        const lcRes = await fetch(`/api/landing/content?country=${activeCountry}`);
                        if (lcRes.ok) {
                            const lcData = await lcRes.json();
                            if (lcData.content) parsed = lcData.content;
                        }
                    } catch { /* fallback below */ }
                }

                // Fallback: load from system_settings (legacy)
                if (!parsed) {
                    const settings = await ApiService.getSystemSettings();
                    if (settings && settings['landing_page_config']) {
                        try { parsed = JSON.parse(settings['landing_page_config']); } catch { }
                    }
                }

                if (parsed) {
                    if (parsed.hero && parsed.hero.labels && !Array.isArray(parsed.hero.labels)) {
                        parsed.hero.labels = DEFAULT_CONTENT.hero.labels;
                    }
                    if (parsed.hero && Array.isArray(parsed.hero.labels)) {
                        parsed.hero.labels = parsed.hero.labels.map((l: any) => {
                            let ic = l.icon;
                            if (ic === '🛡️' || ic?.includes('🛡️')) ic = '<Shield className="w-4 h-4 inline mr-1" />';
                            if (ic === '💨' || ic?.includes('💨')) ic = '<Wind className="w-4 h-4 inline mr-1" />';
                            if (ic === '📡' || ic?.includes('📡')) ic = '<Radio className="w-4 h-4 inline mr-1" />';
                            return { ...l, icon: ic };
                        });
                    }
                    if (parsed.hero && parsed.hero.ctaSecondary && parsed.hero.ctaSecondary.toUpperCase().includes('DEMO')) {
                        parsed.hero.ctaSecondary = 'Gleich Abo kaufen';
                    }
                    if (parsed.hero && parsed.hero.ctaPrimary) {
                        parsed.hero.ctaPrimary = 'Kostenlos registrieren';
                    }
                    setContent(prev => {
                        const merged = { ...prev };
                        if (parsed.hero) merged.hero = { ...prev.hero, ...parsed.hero };
                        if (parsed.featuresSection) merged.featuresSection = { ...prev.featuresSection, ...parsed.featuresSection };
                        if (parsed.features && Array.isArray(parsed.features) && parsed.features.length > 0) {
                            merged.features = parsed.features.map((f: any) => {
                                let ic = f.icon;
                                if (ic === '🗺️' || ic?.includes('🗺️')) ic = '<Map className="w-[1em] h-[1em] inline mr-1 text-emerald-400" />';
                                if (ic === '📍' || ic?.includes('📍')) ic = '<MapPin className="w-[1em] h-[1em] inline mr-1 text-rose-400" />';
                                if (ic === '🔥' || ic?.includes('🔥')) ic = '<Flame className="w-[1em] h-[1em] inline mr-1 text-amber-500" />';
                                if (ic === '✨' || ic?.includes('✨')) ic = '<Sparkles className="w-[1em] h-[1em] inline mr-1 text-purple-400" />';
                                if (ic === '📐' || ic?.includes('📐')) ic = '<Ruler className="w-[1em] h-[1em] inline mr-1 text-yellow-400" />';
                                if (ic === '🤖' || ic?.includes('🤖')) ic = '<Bot className="w-[1em] h-[1em] inline mr-1 text-indigo-400" />';
                                if (f.title === 'FlugBuddys' || f.title?.includes('Buddys')) ic = '<Users className="w-[1em] h-[1em] inline mr-1 text-orange-400" />';
                                if (f.title === 'Flughistorie' || f.title?.includes('historie')) ic = '<Calendar className="w-[1em] h-[1em] inline mr-1 text-sky-400" />';
                                if (ic === '👥' || ic?.includes('👥') || ic?.includes('👨‍👩‍👦') || ic?.includes('👨‍👨‍👦') || ic?.includes('👨‍👩‍👧') || ic?.includes('🧑‍🤝‍🧑')) ic = '<Users className="w-[1em] h-[1em] inline mr-1 text-orange-400" />';
                                if (ic === '📊' || ic?.includes('📊')) ic = '<BarChart className="w-[1em] h-[1em] inline mr-1 text-teal-400" />';
                                if (ic === '⚖️' || ic?.includes('⚖️')) ic = '<Scale className="w-[1em] h-[1em] inline mr-1 text-pink-400" />';
                                if (ic === '🛡️' || ic?.includes('🛡️')) ic = '<Shield className="w-[1em] h-[1em] inline mr-1 text-sky-500" />';
                                if (ic === '💨' || ic?.includes('💨')) ic = '<Wind className="w-[1em] h-[1em] inline mr-1 text-cyan-400" />';
                                if (ic === '📡' || ic?.includes('📡')) ic = '<Radio className="w-[1em] h-[1em] inline mr-1 text-red-500" />';
                                if (ic === '📅' || ic?.includes('📅') || ic?.includes('🗓️')) ic = '<Calendar className="w-[1em] h-[1em] inline mr-1 text-sky-400" />';
                                return { ...f, icon: ic };
                            });
                        }
                        if (parsed.team && parsed.team.title) merged.team = { ...prev.team, ...parsed.team };
                        if (parsed.trophy && parsed.trophy.title) merged.trophy = { ...prev.trophy, ...parsed.trophy };
                        if (parsed.sliceBackgrounds) merged.sliceBackgrounds = parsed.sliceBackgrounds;
                        return merged;
                    });
                }

                // Site Assets (from system_settings)
                try {
                    const settings2 = await ApiService.getSystemSettings();
                    if (settings2 && settings2['site_assets']) {
                        const assets = JSON.parse(settings2['site_assets']) as SiteAssets;
                        setHeroUrl(assets.heroUrl || 'https://skstreckenfliegen.blob.core.windows.net/mediadaten/alps-hero-2.jpg');
                        setHeroBgUrl(assets.heroBgUrl || '');
                        setHeroScale(assets.heroScale || 1);
                        setHeroPos(assets.heroPos || { x: 50, y: 50 });
                        setTeamPhotoUrl(assets.teamPhotoUrl || 'https://skstreckenfliegen.blob.core.windows.net/mediadaten/team-alex.jpg');
                        setTeamPhotoScale(assets.teamPhotoScale || 1);
                        setTeamPhotoPos(assets.teamPhotoPos || { x: 50, y: 50 });
                        
                        setHeroAvatarUrl((assets as any).heroAvatarUrl || '/cmPortraitIQ.jpg');
                        
                        localStorage.setItem('xc_site_assets', settings2['site_assets']);
                    } else {
                        setHeroUrl('https://skstreckenfliegen.blob.core.windows.net/mediadaten/alps-hero-2.jpg');
                        setHeroBgUrl('');
                        setTeamPhotoUrl('https://skstreckenfliegen.blob.core.windows.net/mediadaten/team-alex.jpg');
                        setHeroScale(1); setHeroPos({x:50,y:50}); setTeamPhotoScale(1); setTeamPhotoPos({x:50,y:50});
                    }
                } catch {
                    setHeroUrl('https://skstreckenfliegen.blob.core.windows.net/mediadaten/alps-hero-2.jpg');
                    setHeroBgUrl('');
                    setTeamPhotoUrl('https://skstreckenfliegen.blob.core.windows.net/mediadaten/team-alex.jpg');
                    setHeroScale(1); setHeroPos({x:50,y:50}); setTeamPhotoScale(1); setTeamPhotoPos({x:50,y:50});
                }
            } catch (err) {
                console.error("Failed to load data", err);
            } finally {
                setLoadingPlans(false);
            }
        };

        const handleAssetUpdate = (e: any) => {
            const assets = e.detail as SiteAssets;
            if (assets) {
                if (assets.heroUrl) setHeroUrl(assets.heroUrl);
                if (assets.heroBgUrl !== undefined) setHeroBgUrl(assets.heroBgUrl);
                if (assets.heroScale !== undefined) setHeroScale(assets.heroScale);
                if (assets.heroPos !== undefined) setHeroPos(assets.heroPos);
                
                if (assets.teamPhotoUrl) setTeamPhotoUrl(assets.teamPhotoUrl);
                if (assets.teamPhotoScale !== undefined) setTeamPhotoScale(assets.teamPhotoScale);
                if (assets.teamPhotoPos !== undefined) setTeamPhotoPos(assets.teamPhotoPos);
                if ((assets as any).heroAvatarUrl) setHeroAvatarUrl((assets as any).heroAvatarUrl);
            }
        };
        window.addEventListener('site-assets-update', handleAssetUpdate);

        load();

        return () => window.removeEventListener('site-assets-update', handleAssetUpdate);
    }, []);

    const handleSaveAssetConfig = async (key: 'heroUrl' | 'teamPhotoUrl', scale: number, pos: {x:number,y:number}) => {
        try {
            const settings = await ApiService.getSystemSettings();
            const currentAssets = settings?.['site_assets'] ? JSON.parse(settings['site_assets']) : {};
            const prefix = key === 'heroUrl' ? 'hero' : 'teamPhoto';
            const newAssets = { 
                ...currentAssets,
                [`${prefix}Scale`]: scale,
                [`${prefix}Pos`]: pos
            };
            await ApiService.saveSystemSetting('site_assets', JSON.stringify(newAssets));
            window.dispatchEvent(new CustomEvent('site-assets-update', { detail: newAssets }));
        } catch (e) {
            console.error("Asset config save fail", e);
        }
    };

    // Save Content Handler (for Editable)
    const handleContentUpdate = useCallback(async (newContent: LandingPageContent) => {
        setContent(newContent);
        if (editable) {
            // Persist immediately? Or wait for global save?
            // For now, allow specific 'save' button in Admin Wrapper, but here we just update local state.
            // Actually, let's auto-save to backend if we want "direct edit" feeling, 
            // BUT usually Admin wrapper handles the "Save" action for the whole page.
            // Let's assume AdminLandingPage passes a specialized callback or we expose the state up.
            // Since we don't have a callback prop for content change, we might need one or rely on local state syncing.
            // Wait, this component is used in public too. It shouldn't save directly.
            // Refactor: We need an `onContentChange` prop if used in Admin.
            // Since I cannot change the Props interface easily without check, let's assume `AdminLandingPage`
            // will just mount this and maybe we use a Context or a global store?
            // Simplest: Add `onContentChange` to props?
            // Let's store changes in SystemSettings immediately if editable is true?
            // Better: Just update local state here. AdminWrapper can't see it easily unless lifted up.

            // DECISION: I will assume `AdminLandingPage` will pass `content` and `onContentChange` props in the future refactor.
            // For now, I'll implement internal saving to API if editable is true? 
            // No, that's risky.

            // Let's stick to: This component manages its own state for viewing.
            // For editing, we really should lift state up.
            // Let's modify the props to optional `content` and `onContentChange`.
        }
    }, [editable]);

    // Internal helper to update partial state
    const updateHero = (field: keyof typeof content.hero, val: string) => {
        const newContent = { ...content, hero: { ...content.hero, [field]: val } };
        handleContentUpdate(newContent);
        if (editable) {
            // Dispatch event for Admin parent?
            window.dispatchEvent(new CustomEvent('landing-page-update', { detail: newContent }));
        }
    };

    // Scroll Listener
    useEffect(() => {
        const handleScroll = () => setScrolled(window.scrollY > 50);
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const scrollTo = (id: string) => {
        const el = document.getElementById(id);
        if (el) {
            const headerOffset = 160; // Account for the larger logo header
            const elementPosition = el.getBoundingClientRect().top;
            const offsetPosition = elementPosition + window.scrollY - headerOffset;
            window.scrollTo({
                top: offsetPosition,
                behavior: 'smooth'
            });
        }
    };

    // Drag Logic
    const handleLabelMouseDown = (e: React.MouseEvent, id: string) => {
        if (!editable) return;
        setDraggedLabel(id);
        e.preventDefault();
        e.stopPropagation();
    };

    const handleMouseMove = (e: React.MouseEvent) => {
        if (!draggedLabel || !editable) return;
        const container = document.getElementById('hero-image-container');
        if (!container) return;

        const rect = container.getBoundingClientRect();
        const x = ((e.clientX - rect.left) / rect.width) * 100;
        const y = ((e.clientY - rect.top) / rect.height) * 100;

        // Clamp values
        const clampedX = Math.max(0, Math.min(100, x));
        const clampedY = Math.max(0, Math.min(100, y));

        const newLabels = content.hero.labels.map(l => l.id === draggedLabel ? { ...l, x: clampedX, y: clampedY } : l);
        setContent(prev => ({ ...prev, hero: { ...prev.hero, labels: newLabels } }));
    };

    const handleMouseUp = () => {
        if (draggedLabel) {
            handleContentUpdate(content); // Persist on drop
            if (editable) window.dispatchEvent(new CustomEvent('landing-page-update', { detail: content }));
            setDraggedLabel(null);
        }
    };

    const addHeroLabel = () => {
        const newLabel: HeroLabel = {
            id: 'l' + Date.now(),
            icon: '<MapPin className="w-4 h-4 inline mr-1" />',
            title: 'Neues Label',
            value: 'Wert',
            x: 50,
            y: 50,
            color: 'sky'
        };
        const newLabels = [...content.hero.labels, newLabel];
        const newContent = { ...content, hero: { ...content.hero, labels: newLabels } };
        handleContentUpdate(newContent);
        if (editable) window.dispatchEvent(new CustomEvent('landing-page-update', { detail: newContent }));
    };

    const removeHeroLabel = (id: string) => {
        const newLabels = content.hero.labels.filter(l => l.id !== id);
        const newContent = { ...content, hero: { ...content.hero, labels: newLabels } };
        handleContentUpdate(newContent);
        if (editable) window.dispatchEvent(new CustomEvent('landing-page-update', { detail: newContent }));
    };

    const updateHeroLabel = (id: string, field: keyof HeroLabel, val: any) => {
        const newLabels = content.hero.labels.map(l => l.id === id ? { ...l, [field]: val } : l);
        const newContent = { ...content, hero: { ...content.hero, labels: newLabels } };
        // Debounce or just update local state is fine for text, handled by EditableText logic usually but here passed to setContent
        // Actually EditableText calls onSave, so we can full update
        handleContentUpdate(newContent);
        if (editable) window.dispatchEvent(new CustomEvent('landing-page-update', { detail: newContent }));
    };

    // Feature Drag & Drop
    const handleFeatureDragStart = (e: React.DragEvent, index: number) => {
        if (!editable) return;
        setDraggedFeatureIndex(index);
        e.dataTransfer.effectAllowed = 'move';
        // Make the drag image transparent or look good? Default is fine for start.
    };

    const handleFeatureDragOver = (e: React.DragEvent, index: number) => {
        if (!editable || draggedFeatureIndex === null) return;
        e.preventDefault(); // Necessary to allow dropping
        e.dataTransfer.dropEffect = 'move';
    };

    const handleFeatureDrop = (e: React.DragEvent, targetIndex: number) => {
        if (!editable || draggedFeatureIndex === null) return;
        e.preventDefault();

        if (draggedFeatureIndex === targetIndex) return;

        const newFeatures = [...content.features];
        const [movedItem] = newFeatures.splice(draggedFeatureIndex, 1);
        newFeatures.splice(targetIndex, 0, movedItem);

        handleContentUpdate({ ...content, features: newFeatures });
        if (editable) window.dispatchEvent(new CustomEvent('landing-page-update', { detail: { ...content, features: newFeatures } }));
        setDraggedFeatureIndex(null);
    };

    const editMode = editable ? 'edit' : 'view';

    return (
        <div className="bg-slate-950 min-h-screen text-slate-200 font-sans selection:bg-sky-500/30 selection:text-white relative">
            {iconPickerTarget && (
                <div className="fixed inset-0 z-[6000] flex items-center justify-center">
                    <div className="absolute inset-0 bg-black/50" onClick={() => setIconPickerTarget(null)}></div>

                    {/* ICON PICKER */}
                    {iconPickerTarget.type !== 'badge' && (
                        <IconPicker
                            onClose={() => setIconPickerTarget(null)}
                            onSelect={(icon) => {
                                if (iconPickerTarget.type === 'label') {
                                    updateHeroLabel(content.hero.labels[iconPickerTarget.index].id, 'icon', icon);
                                } else {
                                    const newFeatures = [...content.features];
                                    newFeatures[iconPickerTarget.index].icon = icon;
                                    handleContentUpdate({ ...content, features: newFeatures });
                                    if (editable) window.dispatchEvent(new CustomEvent('landing-page-update', { detail: { ...content, features: newFeatures } }));
                                }
                                setIconPickerTarget(null);
                            }}
                        />
                    )}

                    {/* BADGE PICKER */}
                    {iconPickerTarget.type === 'badge' && (
                        <div className="absolute z-[6000] p-6 bg-slate-900 border border-white/10 rounded-2xl shadow-2xl animate-in fade-in zoom-in-95 min-w-[300px]" onClick={e => e.stopPropagation()}>
                            <div className="flex justify-between items-center mb-4">
                                <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider">Pakete wählen</h4>
                                <button onClick={() => setIconPickerTarget(null)} className="text-slate-500 hover:text-white">✕</button>
                            </div>
                            <div className="space-y-2">
                                {['NEW', 'FREE', ...plans.map(p => p.name).filter(t => t !== 'FREE')].filter((v, i, a) => a.indexOf(v) === i).map(badge => {
                                    const isActive = content.features[iconPickerTarget.index].badges.includes(badge);

                                    // Determine styling
                                    let activeBg = 'bg-sky-600/20';
                                    let activeBorder = 'border-sky-500';
                                    let activeText = 'text-sky-400';

                                    const plan = plans.find(p => p.name === badge);
                                    if (badge === 'NEW') {
                                        activeBg = 'bg-emerald-600/20'; activeBorder = 'border-emerald-500'; activeText = 'text-emerald-400';
                                    } else if (badge === 'FREE') {
                                        activeBg = 'bg-slate-600/20'; activeBorder = 'border-slate-500'; activeText = 'text-slate-400';
                                    } else if (plan?.color) {
                                        if (plan.color === 'amber') { activeBg = 'bg-amber-600/20'; activeBorder = 'border-amber-500'; activeText = 'text-amber-400'; }
                                        else if (plan.color === 'purple') { activeBg = 'bg-purple-600/20'; activeBorder = 'border-purple-500'; activeText = 'text-purple-400'; }
                                        else if (plan.color === 'rose') { activeBg = 'bg-rose-600/20'; activeBorder = 'border-rose-500'; activeText = 'text-rose-400'; }
                                        else if (plan.color === 'emerald') { activeBg = 'bg-emerald-600/20'; activeBorder = 'border-emerald-500'; activeText = 'text-emerald-400'; }
                                        else if (plan.color === 'sky') { activeBg = 'bg-sky-600/20'; activeBorder = 'border-sky-500'; activeText = 'text-sky-400'; }
                                        else if (plan.color === 'slate') { activeBg = 'bg-slate-600/20'; activeBorder = 'border-slate-500'; activeText = 'text-slate-400'; }
                                    }

                                    return (
                                        <button
                                            key={badge}
                                            onClick={() => {
                                                const feature = content.features[iconPickerTarget.index];
                                                let newBadges = [...feature.badges];
                                                if (isActive) {
                                                    newBadges = newBadges.filter(b => b !== badge);
                                                } else {
                                                    newBadges.push(badge);
                                                }
                                                const newFeatures = [...content.features];
                                                newFeatures[iconPickerTarget.index].badges = newBadges;
                                                handleContentUpdate({ ...content, features: newFeatures });
                                                if (editable) window.dispatchEvent(new CustomEvent('landing-page-update', { detail: { ...content, features: newFeatures } }));
                                            }}
                                            className={`w-full flex items-center justify-between p-3 rounded-xl border transition-all ${isActive
                                                ? 'bg-sky-600/20 border-sky-500 text-white'
                                                : 'bg-slate-800 border-white/5 text-slate-400 hover:bg-slate-700'
                                                }`}
                                        >
                                            <span className="font-bold text-sm tracking-widest">{badge}</span>
                                            {isActive && <span className="text-sky-400">✓</span>}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                </div>
            )}
            {/* Sticky Navbar */}
            <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-slate-950/80 backdrop-blur-md border-b border-white/5 py-4' : 'bg-transparent py-6'}`}>
                <div className="container mx-auto px-6 flex justify-between items-center">
                    <div className="flex items-center gap-3 cursor-pointer shrink" onClick={() => scrollTo('home')}>
                        <img src={globalThis.localStorage?.getItem("xc_site_assets") ? (JSON.parse(globalThis.localStorage.getItem("xc_site_assets") as string).logoUrl || "/logo-small.png") : "/logo-small.png"} alt="Logo" className="h-14 sm:h-24 md:h-32 w-auto max-w-[65vw] md:max-w-[400px] object-contain drop-shadow-[0_0_10px_rgba(14,165,233,0.5)] transition-all duration-300" />
                        <span className="text-xl font-black text-white tracking-tighter uppercase hidden sm:block">
                            {globalThis.localStorage?.getItem("xc_site_assets") && JSON.parse(globalThis.localStorage.getItem("xc_site_assets") as string).headerTitle !== undefined ? JSON.parse(globalThis.localStorage.getItem("xc_site_assets") as string).headerTitle : <span>Streckenfliegen<span className="text-sky-500">.app</span></span>}
                        </span>
                    </div>

                    <div className="hidden md:flex gap-6 lg:gap-8 text-xs lg:text-sm font-bold uppercase tracking-widest text-slate-400">
                        {['Features', 'Tutorials', 'Trophy', 'Preise', 'Über uns', 'Kontakt'].map(item => (
                            <button key={item} onClick={() => scrollTo(item.toLowerCase().replace(' ', '-').replace('ü', 'ue'))} className="hover:text-white hover:scale-105 transition-all">{item}</button>
                        ))}
                    </div>

                    <div className="flex gap-4 items-center shrink-0">
                        <button onClick={() => onEnterApp()} className="hidden md:block bg-sky-600 hover:bg-sky-500 text-white px-6 py-3 rounded-xl font-bold shadow-lg shadow-sky-900/40 hover:shadow-sky-500/20 transition-all active:scale-95 text-xs uppercase tracking-widest">
                            Login / App Starten
                        </button>

                        {/* Mobile Hamburger */}
                        <button className="md:hidden text-2xl text-white p-2 shrink-0 hover:bg-white/10 rounded-lg transition-colors ml-2" onClick={() => {
                            const el = document.getElementById('mobile-menu');
                            if (el) el.classList.toggle('hidden');
                        }}>
                            ☰
                        </button>
                    </div>
                </div>

                {/* Mobile Menu Overlay */}
                <div id="mobile-menu" className="hidden absolute top-full left-0 right-0 bg-slate-950 border-b border-white/10 p-6 flex flex-col gap-6 shadow-2xl animate-in slide-in-from-top-4">
                    {['Features', 'Tutorials', 'Trophy', 'Preise', 'Über uns', 'Kontakt'].map(item => (
                        <button key={item} onClick={() => {
                            scrollTo(item.toLowerCase().replace(' ', '-').replace('ü', 'ue'));
                            document.getElementById('mobile-menu')?.classList.add('hidden');
                        }} className="text-slate-300 font-bold uppercase tracking-widest text-lg text-left">{item}</button>
                    ))}
                    <button onClick={() => onEnterApp()} className="bg-sky-600 text-white px-6 py-4 rounded-xl font-bold uppercase tracking-widest text-center">
                        Login / App Starten
                    </button>
                </div>
            </nav>

            
            {sliceOrder.map((sliceId, idx) => (
                <div key={sliceId} id={sliceId === 'hero' ? 'home' : sliceId} className="relative group/slice" style={{ backgroundColor: content.sliceBackgrounds?.[sliceId]?.bgColor || undefined }}>
                    {editable && (
                        <div className="absolute left-2 top-1/2 -translate-y-1/2 flex flex-col gap-1 z-[100] opacity-0 group-hover/slice:opacity-100 transition-opacity">
                            <button onClick={() => moveSlice(idx, -1)} disabled={idx === 0} className="bg-slate-800 text-white p-2 rounded hover:bg-slate-700 shadow-xl border border-white/10 disabled:opacity-30">↑</button>
                            <button onClick={() => moveSlice(idx, 1)} disabled={idx === sliceOrder.length - 1} className="bg-slate-800 text-white p-2 rounded hover:bg-slate-700 shadow-xl border border-white/10 disabled:opacity-30">↓</button>
                            <button onClick={() => setBgEditorOpenFor(bgEditorOpenFor === sliceId ? null : sliceId)} className={`p-2 rounded shadow-xl border transition-all ${bgEditorOpenFor === sliceId ? 'bg-sky-600 text-white border-sky-500' : 'bg-slate-800 text-white hover:bg-slate-700 border-white/10'}`} title="Hintergrund bearbeiten"><Image className="w-4 h-4 inline mr-1" /></button>
                            
                            {bgEditorOpenFor === sliceId && (
                                <div className="absolute left-full top-0 ml-2 bg-slate-900 border border-white/10 p-4 rounded-xl shadow-2xl w-64 animate-in fade-in zoom-in-95 cursor-default text-left">
                                    <div className="text-white font-bold mb-3 text-sm">Hintergrund ({sliceId})</div>
                                    <div className="space-y-4">
                                        <div>
                                            <div className="text-xs text-slate-400 mb-1">Bild-URL</div>
                                            <button 
                                                onClick={() => { setMediaTarget(`sliceBg:${sliceId}`); setMediaModalOpen(true); }}
                                                className="w-full bg-slate-800 hover:bg-sky-600 text-white text-xs py-2 rounded border border-white/10 transition-colors"
                                            >
                                                {content.sliceBackgrounds?.[sliceId]?.bgUrl ? 'Bild ändern' : 'Bild auswählen'}
                                            </button>
                                            {content.sliceBackgrounds?.[sliceId]?.bgUrl && (
                                                <button onClick={() => updateSliceBg(sliceId, { bgUrl: undefined })} className="mt-2 w-full text-center text-red-400 text-[10px] hover:text-red-300">Bild entfernen</button>
                                            )}
                                        </div>
                                        <div>
                                            <div className="text-xs text-slate-400 mb-1 flex justify-between">
                                                <span>Bild Deckkraft</span>
                                                <span>{content.sliceBackgrounds?.[sliceId]?.opacity ?? 20}%</span>
                                            </div>
                                            <input title="Hintergrund Transparenz" type="range" min="0" max="100" value={content.sliceBackgrounds?.[sliceId]?.opacity ?? 20} onChange={(e) => updateSliceBg(sliceId, { opacity: parseInt(e.target.value) })} className="w-full" />
                                        </div>
                                        <div>
                                            <div className="text-xs text-slate-400 mb-1">Grundfarbe (Hex)</div>
                                            <div className="flex items-center gap-2">
                                                <span className="text-slate-500">#</span>
                                                <input type="text" value={(content.sliceBackgrounds?.[sliceId]?.bgColor || '').replace('#', '')} onChange={(e) => updateSliceBg(sliceId, { bgColor: e.target.value ? '#' + e.target.value.replace('#', '') : undefined })} placeholder="020617" className="w-full bg-slate-800 border border-white/10 p-1.5 rounded text-xs text-white" />
                                            </div>
                                        </div>
                                    </div>
                                    <button onClick={() => setBgEditorOpenFor(null)} className="mt-4 w-full bg-sky-600 hover:bg-sky-500 text-white text-xs py-1.5 rounded font-bold transition-colors">Fertig</button>
                                </div>
                            )}
                        </div>
                    )}
                    
                    {content.sliceBackgrounds?.[sliceId]?.bgUrl && sliceId !== 'hero' && (
                        <div className="absolute inset-0 overflow-hidden z-0 pointer-events-none">
                            <img src={content.sliceBackgrounds[sliceId].bgUrl} alt="Background" className="w-full h-full object-cover" style={{ opacity: (content.sliceBackgrounds[sliceId].opacity ?? 20) / 100 }} />
                        </div>
                    )}
                    
                    {sliceId === 'hero' && (
                        <>
{/* Hero Section */}
            <section id="home" className={`relative min-h-[100dvh] flex flex-col ${idx === 0 ? 'pt-[140px] pb-10 lg:pt-[160px] lg:pb-0' : 'py-20'} overflow-hidden group/hero ${content.sliceBackgrounds?.hero?.bgColor ? 'bg-transparent' : ''}`}>
                {/* Abstract Background OR Image Background */}
                {heroBgUrl || content.sliceBackgrounds?.hero?.bgUrl ? (
                    <img src={content.sliceBackgrounds?.hero?.bgUrl || heroBgUrl} alt="Hero Background" className="absolute inset-0 w-full h-full object-cover z-0" style={{ opacity: (content.sliceBackgrounds?.hero?.opacity ?? 20) / 100 }} />
                ) : (
                    <>
                        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,_var(--tw-gradient-stops))] from-sky-900/20 via-slate-950 to-slate-950 z-0"></div>
                        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-sky-600/10 rounded-full blur-[100px] -translate-y-1/2 translate-x-1/2 z-0"></div>
                    </>
                )}
                
                {editable && (
                    <div className="absolute top-24 left-6 z-[60] opacity-0 group-hover/hero:opacity-100 transition-opacity">
                        <button
                            className="bg-slate-800/90 border border-white/10 hover:bg-sky-600 hover:border-sky-500 text-white rounded-xl px-4 py-2 text-sm font-bold shadow-2xl backdrop-blur flex items-center justify-center gap-2 transition-all"
                            onClick={() => { setMediaTarget('heroBgUrl'); setMediaModalOpen(true); }}
                        >
                            <Image className="w-4 h-4 inline mr-1" /> Layer Hintergrundbild
                        </button>
                    </div>
                )}

                <div className="container mx-auto px-6 relative z-10 grid grid-cols-1 lg:grid-cols-2 gap-12 my-auto items-center">
                    <div className="space-y-8">
                        <div className="flex items-center justify-between gap-4 w-full max-w-full sm:w-auto sm:inline-flex bg-sky-900/30 border border-sky-500/30 rounded-3xl p-3 px-5 sm:px-6 leading-[0.85] text-[5px] sm:text-xs sm:leading-normal font-bold text-sky-400 uppercase tracking-widest animate-in fade-in slide-in-from-bottom-4 duration-700 text-left shadow-2xl backdrop-blur-sm">
                            <div className="flex-1 min-w-0 py-1 max-sm:[&_.parsed-size-1-5em]:!text-[3em]">
                                <EditableText value={content.hero.subtitle} onSave={v => updateHero('subtitle', v)} mode={editMode} />
                            </div>
                            {editable ? (
                                <button onClick={() => { setMediaTarget('heroAvatarUrl'); setMediaModalOpen(true); }} className="relative group/edit flex-shrink-0 lg:hidden" title="Bild ändern">
                                    <img src={heroAvatarUrl} alt="Markenbotschafter" className="w-40 h-40 sm:w-48 sm:h-48 rounded-full object-cover border-2 border-sky-500/50 shadow-lg group-hover/edit:opacity-80 transition-opacity" />
                                    <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover/edit:opacity-100 transition-opacity">
                                        <div className="bg-sky-600/90 text-white rounded-full p-2 shadow-lg"><Camera className="w-4 h-4" /></div>
                                    </div>
                                </button>
                            ) : (
                                <img src={heroAvatarUrl} alt="Markenbotschafter" className="w-40 h-40 sm:w-48 sm:h-48 rounded-full object-cover border-2 border-sky-500/50 flex-shrink-0 lg:hidden shadow-lg" />
                            )}
                        </div>
                        <div className="text-5xl sm:text-7xl font-black text-white leading-tight tracking-tight animate-in fade-in slide-in-from-bottom-6 duration-700 delay-100">
                            {/* Color Picker Backdrop */}
                            {editable && activeColorWord && (
                                <div className="fixed inset-0 z-[4999]" onClick={() => { setActiveColorWord(null); setHexInput(''); }} />
                            )}
                            <EditableText
                                value={content.hero.title}
                                onSave={v => updateHero('title', v)}
                                mode={editMode}
                                multiline
                                className="whitespace-pre-line"
                                tag="h1"
                                renderView={(text) => (
                                    <>{text.split(/(\s+|\n)/).map((word, i) => {
                                        const trimmed = word.trim();
                                        if (!trimmed) return <span key={i}>{word.includes('\n') ? <br /> : ' '}</span>;
                                        const color = content.hero.titleColors?.[trimmed] || content.hero.titleColors?.[trimmed.toLowerCase()];
                                        return (
                                            <span
                                                key={i}
                                                className={editable ? 'cursor-pointer hover:ring-2 hover:ring-sky-500/50 rounded px-0.5 transition-all relative inline-block' : undefined}
                                                // eslint-disable-next-line react/forbid-component-props
                                                style={color ? { color } : undefined}
                                                onClick={editable ? (e) => {
                                                    e.stopPropagation();
                                                    setActiveColorWord(prev => prev === trimmed ? null : trimmed);
                                                    setHexInput(color || '');
                                                } : undefined}
                                            >
                                                {word}
                                                {editable && activeColorWord === trimmed && (
                                                    <div className="absolute top-full left-0 z-[5000] mt-2 p-3 bg-slate-900 border border-white/10 rounded-xl shadow-2xl min-w-[220px]" onClick={e => e.stopPropagation()}>
                                                        <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">Farbe: {trimmed}</div>
                                                        <div className="flex flex-wrap gap-1.5 mb-3">
                                                            {[
                                                                { c: '', n: 'Standard (Weiss)' },
                                                                { c: '#38bdf8', n: 'Sky' },
                                                                { c: '#818cf8', n: 'Indigo' },
                                                                { c: '#a78bfa', n: 'Violet' },
                                                                { c: '#f472b6', n: 'Pink' },
                                                                { c: '#fb923c', n: 'Orange' },
                                                                { c: '#fbbf24', n: 'Amber' },
                                                                { c: '#4ade80', n: 'Grün' },
                                                                { c: '#f87171', n: 'Rot' },
                                                                { c: '#22d3ee', n: 'Cyan' },
                                                                { c: '#e879f9', n: 'Fuchsia' },
                                                                { c: '#94a3b8', n: 'Slate' },
                                                            ].map(({ c, n }) => (
                                                                <button
                                                                    key={c || 'default'}
                                                                    title={n}
                                                                    className={`w-7 h-7 rounded-full border-2 hover:scale-110 transition-all ${(c === '' && !color) || c === color
                                                                        ? 'border-sky-400 ring-2 ring-sky-400/50'
                                                                        : 'border-white/10 hover:border-white/50'
                                                                        }`}
                                                                    // eslint-disable-next-line react/forbid-component-props
                                                                    style={{ backgroundColor: c || '#ffffff' }}
                                                                    onClick={() => {
                                                                        const newColors = { ...(content.hero.titleColors || {}) };
                                                                        if (!c) { delete newColors[trimmed]; delete newColors[trimmed.toLowerCase()]; }
                                                                        else { newColors[trimmed] = c; }
                                                                        const newContent = { ...content, hero: { ...content.hero, titleColors: Object.keys(newColors).length > 0 ? newColors : undefined } };
                                                                        handleContentUpdate(newContent);
                                                                        window.dispatchEvent(new CustomEvent('landing-page-update', { detail: newContent }));
                                                                        setActiveColorWord(null);
                                                                        setHexInput('');
                                                                    }}
                                                                />
                                                            ))}
                                                        </div>
                                                        <div className="flex gap-2 items-center">
                                                            <span className="text-slate-500 text-xs">#</span>
                                                            <input
                                                                type="text"
                                                                placeholder="z.B. ff6b35"
                                                                value={hexInput.replace('#', '')}
                                                                onChange={e => setHexInput(e.target.value.replace('#', ''))}
                                                                className="flex-1 bg-slate-800 border border-white/10 rounded-lg px-2 py-1.5 text-xs text-white font-mono focus:border-sky-500 focus:outline-none"
                                                                maxLength={6}
                                                                title="Hex-Farbwert eingeben"
                                                            />
                                                            <button
                                                                className="bg-sky-600 hover:bg-sky-500 text-white text-xs font-bold px-3 py-1.5 rounded-lg transition-all disabled:opacity-30 disabled:cursor-not-allowed"
                                                                disabled={!/^[0-9a-fA-F]{3,6}$/.test(hexInput.replace('#', ''))}
                                                                onClick={() => {
                                                                    const hex = '#' + hexInput.replace('#', '');
                                                                    const newColors = { ...(content.hero.titleColors || {}), [trimmed]: hex };
                                                                    const newContent = { ...content, hero: { ...content.hero, titleColors: newColors } };
                                                                    handleContentUpdate(newContent);
                                                                    window.dispatchEvent(new CustomEvent('landing-page-update', { detail: newContent }));
                                                                    setActiveColorWord(null);
                                                                    setHexInput('');
                                                                }}
                                                            >OK</button>
                                                        </div>
                                                        {hexInput && /^[0-9a-fA-F]{3,6}$/.test(hexInput.replace('#', '')) && (
                                                            // eslint-disable-next-line react/forbid-component-props
                                                            <div className="mt-2 h-4 rounded" style={{ backgroundColor: '#' + hexInput.replace('#', '') }} />
                                                        )}
                                                    </div>
                                                )}
                                            </span>
                                        );
                                    })}</>
                                )}
                            />
                        </div>
                        <div className="text-lg text-slate-400 max-w-xl leading-relaxed animate-in fade-in slide-in-from-bottom-8 duration-700 delay-200">
                            <EditableText
                                value={content.hero.description}
                                onSave={v => updateHero('description', v)}
                                mode={editMode}
                                multiline
                                tag="p"
                            />
                        </div>
                        <div className="flex flex-col sm:flex-row gap-4 pt-4 animate-in fade-in slide-in-from-bottom-10 duration-700 delay-300">
                            <button onClick={() => onEnterApp('register')} title={content.hero.ctaPrimary} className="w-full sm:w-auto bg-white text-slate-950 px-4 py-3 sm:px-8 sm:py-4 rounded-xl font-black text-xs sm:text-sm uppercase tracking-widest hover:bg-sky-50 transition-all shadow-xl hover:scale-105 active:scale-95 text-center">
                                <EditableText value={content.hero.ctaPrimary} onSave={v => updateHero('ctaPrimary', v)} mode={editMode} tag="span" />
                            </button>
                            <button onClick={() => onEnterApp('buy')} title={content.hero.ctaSecondary} className="w-full sm:w-auto bg-emerald-500 text-white px-4 py-3 sm:px-8 sm:py-4 rounded-xl font-black text-xs sm:text-sm uppercase tracking-widest hover:bg-emerald-400 transition-all shadow-[0_0_20px_rgba(16,185,129,0.3)] hover:scale-105 active:scale-95 text-center">
                                <EditableText value={content.hero.ctaSecondary} onSave={v => updateHero('ctaSecondary', v)} mode={editMode} tag="span" />
                            </button>
                        </div>
                    </div>
                    <div className="relative h-[600px] hidden lg:block animate-in fade-in zoom-in-95 duration-1000 delay-300">
                        {/* Static Hero Image */}
                        <div className="absolute inset-0 bg-slate-900 rounded-3xl border border-white/10 shadow-2xl overflow-hidden group">
                            {heroUrl && (
                                <EditableImage
                                    src={heroUrl}
                                    alt="Streckenfliegen Hero Presentation"
                                    editable={editable}
                                    initialScale={heroScale}
                                    initialPos={heroPos}
                                    onSave={(scale, pos) => handleSaveAssetConfig('heroUrl', scale, pos)}
                                    className="absolute inset-0 w-full h-full"
                                    imageClassName="w-full h-full object-cover opacity-90 transition-opacity duration-1000 group-hover:opacity-100"
                                />
                            )}
                            {editable && (
                                <button
                                    className="absolute inset-0 m-auto w-48 h-12 bg-sky-600/90 hover:bg-sky-500 text-white rounded-xl text-sm font-bold shadow-2xl backdrop-blur flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity"
                                    onClick={() => { setMediaTarget('heroUrl'); setMediaModalOpen(true); }}
                                >
                                    <Camera className="w-4 h-4 inline mr-1" /> Bild-URL ändern
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </section>
                        </>
                    )}
                    {sliceId === 'ambassador' && (
                        <AmbassadorSlice editable={editable} bgConfig={content.sliceBackgrounds?.ambassador} isFirst={idx === 0} />
                    )}
                    {sliceId === 'features' && (
                        <>
{/* About / Features Section */}
            <section id="features" className={`py-24 relative overflow-hidden ${content.sliceBackgrounds?.features?.bgColor || content.sliceBackgrounds?.features?.bgUrl ? 'bg-transparent' : 'bg-slate-950'}`}>
                <div className="absolute top-1/2 left-0 w-full h-[1px] bg-gradient-to-r from-transparent via-white/10 to-transparent z-0"></div>
                <div className="container mx-auto px-6 relative z-10">
                    <div className="text-center mb-20">
                        <h2 className="text-3xl font-black text-white uppercase tracking-tight mb-4">
                            <EditableText
                                value={content.featuresSection?.title || 'Warum Streckenfliegen.app?'}
                                onSave={v => {
                                    const newContent = { ...content };
                                    if (!newContent.featuresSection) newContent.featuresSection = { title: '', subtitle: '' };
                                    newContent.featuresSection.title = v;
                                    handleContentUpdate(newContent);
                                    if (editable) window.dispatchEvent(new CustomEvent('landing-page-update', { detail: newContent }));
                                }}
                                mode={editMode}
                            />
                        </h2>
                        <p className="text-slate-400 max-w-2xl mx-auto">
                            <EditableText
                                value={content.featuresSection?.subtitle || 'Die zentrale Plattform für den gesamten Alpenraum (CH/DE/AT).'}
                                onSave={v => {
                                    const newContent = { ...content };
                                    if (!newContent.featuresSection) newContent.featuresSection = { title: '', subtitle: '' };
                                    newContent.featuresSection.subtitle = v;
                                    handleContentUpdate(newContent);
                                    if (editable) window.dispatchEvent(new CustomEvent('landing-page-update', { detail: newContent }));
                                }}
                                mode={editMode}
                            />
                        </p>
                    </div>

                    {/* Features Grid - Supports up to 12 dynamically */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {content.features.map((feature, i) => (
                            <div
                                key={i}
                                draggable={editable}
                                onDragStart={(e) => handleFeatureDragStart(e, i)}
                                onDragOver={(e) => handleFeatureDragOver(e, i)}
                                onDrop={(e) => handleFeatureDrop(e, i)}
                                className={`bg-slate-900/50 border border-white/5 p-8 rounded-3xl transition-all group relative overflow-hidden ${editable ? 'cursor-grab active:cursor-grabbing hover:border-sky-500/50' : 'hover:bg-slate-900 hover:border-sky-500/30'
                                    } ${draggedFeatureIndex === i ? 'opacity-50 border-dashed border-sky-500' : ''}`}
                            >
                                {editable && (
                                    <div className="absolute top-2 right-2 flex gap-2">
                                        <button className="text-xs bg-red-500/20 text-red-500 px-2 py-1 rounded hover:bg-red-500/50"
                                            title="Entfernen"
                                            onClick={() => {
                                                const newFeatures = [...content.features];
                                                newFeatures.splice(i, 1);
                                                handleContentUpdate({ ...content, features: newFeatures });
                                                if (editable) window.dispatchEvent(new CustomEvent('landing-page-update', { detail: { ...content, features: newFeatures } }));
                                            }}>✖</button>
                                    </div>
                                )}
                                <div
                                    className={`text-5xl mb-6 group-hover:scale-110 transition-transform duration-300 ${editable ? 'cursor-pointer hover:opacity-80' : ''}`}
                                    onClick={(e) => {
                                        if (editable) {
                                            e.stopPropagation();
                                            setIconPickerTarget({ type: 'feature', index: i });
                                        }
                                    }}
                                >
                                    {renderLucideIcon(feature.icon)}
                                </div>
                                <h3 className="text-xl font-bold text-white mb-3">
                                    <EditableText value={feature.title} onSave={v => {
                                        const newFeatures = [...content.features];
                                        newFeatures[i].title = v;
                                        handleContentUpdate({ ...content, features: newFeatures });
                                        if (editable) window.dispatchEvent(new CustomEvent('landing-page-update', { detail: { ...content, features: newFeatures } }));
                                    }} mode={editMode} />
                                </h3>
                                <div className="text-slate-400 text-sm leading-relaxed mb-6">
                                    <EditableText value={feature.description} onSave={v => {
                                        const newFeatures = [...content.features];
                                        newFeatures[i].description = v;
                                        handleContentUpdate({ ...content, features: newFeatures });
                                        if (editable) window.dispatchEvent(new CustomEvent('landing-page-update', { detail: { ...content, features: newFeatures } }));
                                    }} mode={editMode} multiline />
                                </div>
                                <div className={`flex gap-2 flex-wrap min-h-[24px] ${editable ? 'cursor-pointer hover:bg-white/5 rounded-lg p-1 -m-1 transition-colors' : ''}`}
                                    onClick={(e) => {
                                        if (editable) {
                                            e.stopPropagation();
                                            // Toggle Badge Picker (using existing iconPickerTarget state structure or new one? Let's assume new one or piggyback)
                                            // Actually, let's just use a simple local state for this popover, or piggyback on a new state
                                            // For simplicity, let's reuse iconPickerTarget but with a new type 'badge'
                                            setIconPickerTarget({ type: 'badge', index: i });
                                        }
                                    }}
                                >
                                    {feature.badges.length === 0 && editable && <span className="text-xs text-slate-600 italic">Keine Pakete (Klicken zum Ändern)</span>}
                                    {Array.from(new Set(feature.badges))
                                        .filter(b => {
                                            if (loadingPlans) return true; // Show all while loading to prevent flickering
                                            const validBadges = ['NEW', 'FREE', ...plans.map(p => p.name)];
                                            return validBadges.includes(b as string);
                                        })
                                        .map((b, bi) => {
                                            let badgeClass = 'border-sky-500/50 text-sky-500 bg-sky-500/10'; // Default
                                            const plan = plans.find(p => p.name === b);

                                            if (b === 'FREE') badgeClass = 'border-slate-700 text-slate-500';
                                            else if (b === 'NEW') badgeClass = 'border-emerald-500 text-emerald-500 bg-emerald-500/10';
                                            else if (plan?.color) {
                                                if (plan.color === 'amber') badgeClass = 'border-amber-500/50 text-amber-500 bg-amber-500/10';
                                                else if (plan.color === 'purple') badgeClass = 'border-purple-500/50 text-purple-500 bg-purple-500/10';
                                                else if (plan.color === 'rose') badgeClass = 'border-rose-500/50 text-rose-500 bg-rose-500/10';
                                                else if (plan.color === 'emerald') badgeClass = 'border-emerald-500/50 text-emerald-500 bg-emerald-500/10';
                                                else if (plan.color === 'sky') badgeClass = 'border-sky-500/50 text-sky-500 bg-sky-500/10';
                                                else if (plan.color === 'slate') badgeClass = 'border-slate-500/50 text-slate-500 bg-slate-500/10';
                                            }

                                            return (
                                                <span key={bi} className={`text-[10px] font-black uppercase tracking-widest px-2 py-1 rounded border ${badgeClass}`}>
                                                    {b}
                                                </span>
                                            );
                                        })}
                                </div>
                            </div>
                        ))}
                        {editable && (
                            <button onClick={() => {
                                const newFeatures = [...content.features, { icon: '✨', title: 'Neues Feature', description: 'Beschreibung hier...', badges: ['NEW'] }];
                                handleContentUpdate({ ...content, features: newFeatures });
                                window.dispatchEvent(new CustomEvent('landing-page-update', { detail: { ...content, features: newFeatures } }));
                            }} className="bg-slate-900/30 border border-dashed border-white/10 p-8 rounded-3xl hover:bg-slate-900 hover:border-sky-500/50 transition-all flex items-center justify-center flex-col gap-4 group">
                                <div className="text-3xl text-slate-500 group-hover:text-white">+</div>
                                <div className="text-sm font-bold text-slate-500">Feature hinzufügen</div>
                            </button>
                        )}
                    </div>
                </div>
            </section>
                        </>
                    )}
                    {sliceId === 'tutorials' && (
                        <>
{/* VIDEO TUTORIALS SECTION */}
            <div id="tutorials">
                <VideoTutorialsSlice editable={editable} plans={plans} bgConfig={content.sliceBackgrounds?.tutorials} />
            </div>
                        </>
                    )}
                    {sliceId === 'trophy' && (
                        <>
{/* TROPHY SECTION */}
            <section id="trophy" className="py-24 relative overflow-hidden">
                {!content.sliceBackgrounds?.trophy?.bgColor && !content.sliceBackgrounds?.trophy?.bgUrl && (
                    <div className="absolute inset-0 bg-gradient-to-b from-slate-950 via-amber-950/20 to-slate-950"></div>
                )}

                {/* Gold Glow Effect */}
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-amber-500/10 rounded-full blur-[120px] pointer-events-none z-0"></div>

                <div className="container mx-auto px-6 relative z-10">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
                        <div className="order-2 lg:order-1 relative">
                            {/* Graphic Representation of Podium/Trophy */}
                            <div className="relative aspect-square max-w-[500px] mx-auto">
                                <div className="absolute inset-0 bg-gradient-to-tr from-amber-500/20 to-yellow-200/5 rounded-full animate-pulse-slow blur-3xl"></div>
                                <div className="relative z-10 w-full h-full flex items-center justify-center">
                                    <div className="drop-shadow-[0_0_50px_rgba(245,158,11,0.5)] animate-bounce-slow text-amber-500"><Trophy size={150} strokeWidth={1} /></div>
                                </div>
                                {/* Floating Badges for Classes */}
                                <div className="absolute top-10 left-0 bg-slate-900/80 backdrop-blur border border-amber-500/30 px-4 py-2 rounded-xl transform -rotate-6 shadow-xl">
                                    <span className="text-amber-400 font-black text-xl">EN-A</span>
                                </div>
                                <div className="absolute top-20 right-10 bg-slate-900/80 backdrop-blur border border-amber-500/30 px-4 py-2 rounded-xl transform rotate-12 shadow-xl">
                                    <span className="text-amber-400 font-black text-xl">EN-B</span>
                                </div>
                                <div className="absolute bottom-20 left-10 bg-slate-900/80 backdrop-blur border border-amber-500/30 px-4 py-2 rounded-xl transform rotate-3 shadow-xl">
                                    <span className="text-amber-400 font-black text-xl">EN-C</span>
                                </div>
                                <div className="absolute bottom-10 right-20 bg-slate-900/80 backdrop-blur border border-amber-500/30 px-4 py-2 rounded-xl transform -rotate-3 shadow-xl">
                                    <span className="text-amber-400 font-black text-xl">EN-D</span>
                                </div>
                            </div>
                        </div>

                        <div className="order-1 lg:order-2 space-y-8 text-center lg:text-left">
                            <div className="inline-block bg-amber-500/10 border border-amber-500/30 text-amber-500 px-4 py-1.5 rounded-full text-xs font-black uppercase tracking-widest">
                                <EditableText value={content.trophy.subtitle} onSave={v => {
                                    const newT = { ...content.trophy, subtitle: v };
                                    handleContentUpdate({ ...content, trophy: newT });
                                    if (editable) window.dispatchEvent(new CustomEvent('landing-page-update', { detail: { ...content, trophy: newT } }));
                                }} mode={editMode} />
                            </div>

                            <h2 className="text-4xl md:text-5xl font-black text-white uppercase tracking-tight leading-none">
                                <EditableText value={content.trophy.title} onSave={v => {
                                    const newT = { ...content.trophy, title: v };
                                    handleContentUpdate({ ...content, trophy: newT });
                                    if (editable) window.dispatchEvent(new CustomEvent('landing-page-update', { detail: { ...content, trophy: newT } }));
                                }} mode={editMode} multiline className="whitespace-pre-line" />
                            </h2>

                            <div className="flex items-center justify-center lg:justify-start gap-4">
                                <EditableText
                                    value={content.trophy.percentage}
                                    className="text-6xl font-black text-transparent bg-clip-text bg-gradient-to-r from-amber-300 via-yellow-500 to-amber-600"
                                    onSave={v => {
                                        const newT = { ...content.trophy, percentage: v };
                                        handleContentUpdate({ ...content, trophy: newT });
                                        if (editable) window.dispatchEvent(new CustomEvent('landing-page-update', { detail: { ...content, trophy: newT } }));
                                    }} mode={editMode}
                                />
                                <div className="text-left leading-tight">
                                    <div className="text-white font-bold uppercase tracking-wider">der Einnahmen</div>
                                    <div className="text-slate-400 text-sm">für den Sport</div>
                                </div>
                            </div>

                            <div className="text-slate-300 text-lg leading-relaxed max-w-xl mx-auto lg:mx-0">
                                <EditableText value={content.trophy.description} onSave={v => {
                                    const newT = { ...content.trophy, description: v };
                                    handleContentUpdate({ ...content, trophy: newT });
                                    if (editable) window.dispatchEvent(new CustomEvent('landing-page-update', { detail: { ...content, trophy: newT } }));
                                }} mode={editMode} multiline />
                            </div>

                            <div className="bg-slate-900/50 border border-white/10 rounded-2xl p-6 text-sm text-slate-400">
                                <strong className="text-white block mb-2 uppercase tracking-wide">Reglement</strong>
                                <EditableText value={content.trophy.placement} onSave={v => {
                                    const newT = { ...content.trophy, placement: v };
                                    handleContentUpdate({ ...content, trophy: newT });
                                    if (editable) window.dispatchEvent(new CustomEvent('landing-page-update', { detail: { ...content, trophy: newT } }));
                                }} mode={editMode} multiline />
                            </div>
                        </div>
                    </div>
                </div>
            </section>
                        </>
                    )}
                    {sliceId === 'pricing' && (
                        <>
{/* Pricing Section */}

            <section id="preise" className={`py-24 relative ${content.sliceBackgrounds?.pricing?.bgColor || content.sliceBackgrounds?.pricing?.bgUrl ? 'bg-transparent' : 'bg-black/20'}`}>
                <div className="container mx-auto px-6 relative z-10">
                    <div className="text-center mb-20">
                        <h2 className="text-3xl font-black text-white uppercase tracking-tight mb-4">Wähle deinen Plan</h2>
                        <p className="text-slate-400">Faire Preise für maximale Airtime.</p>
                        <p className="text-xs text-slate-500 mt-2"><MapPin className="w-4 h-4 inline mr-1" /> Preise für {geoCountry === 'DE' ? 'Deutschland' : geoCountry === 'AT' ? 'Österreich' : 'die Schweiz'} · inkl. MwSt</p>
                    </div>

                    <div className="flex flex-wrap lg:flex-nowrap justify-center gap-6 max-w-7xl mx-auto items-stretch">
                        {loadingPlans ? (
                            <div className="w-full text-center text-slate-500 italic">Lade Abo-Optionen...</div>
                        ) : plans.length === 0 ? (
                            <div className="w-full text-center text-slate-500">Keine öffentlichen Pläne verfügbar.</div>
                        ) : (
                            plans.filter(p => p.type !== 'test' && p.is_public !== false).sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0) || (a.price || 0) - (b.price || 0)).map((plan) => {
                                const isRecommended = plan.type === 'yearly' || plan.name.toLowerCase().includes('jahr');
                                const isFree = Number(plan.price) === 0;

                                return (
                                    <div key={plan.id} className={`flex-1 min-w-[240px] max-w-[340px] ${isRecommended
                                        ? 'bg-slate-900 border-sky-500 shadow-2xl shadow-sky-900/40 relative z-10'
                                        : 'bg-slate-900/60 border-white/10 opacity-90 hover:opacity-100 hover:border-white/20'
                                        } border rounded-3xl overflow-hidden transition-all flex flex-col group`}>

                                        {/* Gradient Bar Top - Radius fixed by overflow-hidden on parent */}
                                        <div className={`h-2 w-full bg-gradient-to-r ${plan.color === 'emerald' ? 'from-emerald-500 to-teal-600' :
                                            plan.color === 'sky' ? 'from-sky-500 to-blue-600' :
                                                plan.color === 'amber' ? 'from-amber-500 to-orange-600' :
                                                    plan.color === 'purple' ? 'from-purple-500 to-indigo-600' :
                                                        plan.color === 'rose' ? 'from-rose-500 to-pink-600' :
                                                            plan.color === 'slate' ? 'from-slate-500 to-slate-400' :
                                                                plan.name.toLowerCase().includes('free') ? 'from-slate-600 to-slate-500' :
                                                                    isRecommended ? 'from-sky-500 to-blue-600' :
                                                                        'from-purple-500 to-indigo-600'
                                            }`}></div>

                                        <div className="p-8 flex-1 flex flex-col relative">

                                            {(() => {
                                                let dynamicDiscount = plan.discount_percentage || 0;
                                                let dynamicOldPriceCHF = 0;
                                                let hasDynamicDiscount = false;
                                                
                                                if (plan.type === 'yearly') {
                                                    const baseName = plan.name.replace(/jährlich/i, '').trim();
                                                    let monthlyPlan = plans.find(p => p.type === 'monthly' && p.name.replace(/monatlich/i, '').trim() === baseName);
                                                    if (!monthlyPlan) {
                                                        monthlyPlan = plans.find(p => p.type === 'monthly' && p.price > 0 && p.is_public !== false);
                                                    }
                                                    
                                                    if (monthlyPlan && monthlyPlan.price > 0 && plan.price > 0) {
                                                        const yearlyEquivalent = monthlyPlan.price * 12;
                                                        if (yearlyEquivalent > plan.price) {
                                                            dynamicOldPriceCHF = yearlyEquivalent;
                                                            dynamicDiscount = Math.round((1 - (plan.price / yearlyEquivalent)) * 100);
                                                            hasDynamicDiscount = true;
                                                        }
                                                    }
                                                }
                                                
                                                const netCHF = Number(plan.price) || 0;
                                                let netPriceDisplay: number, symbol: string, vatRate: number;
                                                let oldPriceDisplay: number | null = null;
                                                
                                                if (geoCountry === 'DE') {
                                                    vatRate = pricingConfig.vat_rate_de;
                                                    netPriceDisplay = Math.round(netCHF * pricingConfig.exchange_rate_eur_chf * 100) / 100;
                                                    if (hasDynamicDiscount) oldPriceDisplay = Math.round(dynamicOldPriceCHF * pricingConfig.exchange_rate_eur_chf * 100) / 100;
                                                    symbol = '€';
                                                } else if (geoCountry === 'AT') {
                                                    vatRate = pricingConfig.vat_rate_at;
                                                    netPriceDisplay = Math.round(netCHF * pricingConfig.exchange_rate_eur_chf * 100) / 100;
                                                    if (hasDynamicDiscount) oldPriceDisplay = Math.round(dynamicOldPriceCHF * pricingConfig.exchange_rate_eur_chf * 100) / 100;
                                                    symbol = '€';
                                                } else {
                                                    vatRate = pricingConfig.vat_rate_ch;
                                                    netPriceDisplay = Math.round(netCHF * 100) / 100;
                                                    if (hasDynamicDiscount) oldPriceDisplay = Math.round(dynamicOldPriceCHF * 100) / 100;
                                                    symbol = 'CHF';
                                                }

                                                if (!hasDynamicDiscount && plan.discount_percentage && plan.discount_percentage > 0) {
                                                    oldPriceDisplay = netPriceDisplay / (1 - (plan.discount_percentage / 100));
                                                }

                                                return (
                                                    <>
                                                        <div className="flex justify-between items-start mb-2 relative z-10">
                                                            <div>
                                                                <h3 className="text-2xl font-black text-white uppercase tracking-tight">{plan.name}</h3>
                                                                <span className="text-[10px] font-black uppercase tracking-widest text-slate-500">{plan.type === 'monthly' ? 'Monatlich' : plan.type === 'yearly' ? 'Jährlich' : 'Test'}</span>
                                                            </div>
                                                        </div>

                                                        <div className="mb-6 relative z-10">
                                                            {/* Price with MwSt and Currency */}
                                                            {oldPriceDisplay && (
                                                                <div className="text-slate-500 text-sm font-bold strike-through decoration-red-500/50 mb-[-4px]">
                                                                    Statt {symbol} {Math.floor(oldPriceDisplay)}.-
                                                                </div>
                                                            )}
                                                            <div className="font-black text-white text-4xl">
                                                                {symbol} {Math.floor(netPriceDisplay)}.- <span className="text-sm font-normal text-slate-500 whitespace-nowrap">/&nbsp;{plan.type === 'yearly' ? 'Jahr' : 'Monat'}</span>
                                                            </div>
                                                            <div className="text-xs text-slate-500 mt-1">
                                                                exkl. MwSt ({symbol} {(symbol === 'CHF' ? (Math.round(netPriceDisplay * (1 + vatRate / 100) * 20) / 20) : (netPriceDisplay * (1 + vatRate / 100))).toFixed(2)} inkl. {vatRate}% MwSt)
                                                            </div>
                                                        </div>
                                                    </>
                                                );
                                            })()}

                                            {/* Subscription Details (Duration/Notice/Limit) - Like Admin Panel */}
                                            <div className="space-y-2 mb-6 text-xs text-slate-400 border-b border-white/5 pb-6">
                                                {plan.duration && (
                                                    <div className="flex justify-between items-start gap-2">
                                                        <span className="shrink-0 whitespace-nowrap">⏳ Laufzeit:</span>
                                                        <span className="font-bold text-slate-300 text-right whitespace-nowrap">{plan.duration}</span>
                                                    </div>
                                                )}
                                                {plan.notice_period && (
                                                    <div className="flex justify-between items-start gap-2">
                                                        <span className="shrink-0 whitespace-nowrap">📜 Kündigung:</span>
                                                        <span className="font-bold text-slate-300 text-right">{plan.notice_period}</span>
                                                    </div>
                                                )}
                                                <div className="flex justify-between items-start gap-2 text-yellow-500">
                                                    <span className="shrink-0 whitespace-nowrap">⚡ Limit:</span>
                                                    <span className="font-bold text-right">{plan.monthly_limit === -1 ? 'Keine Tokens verfügbar' : (plan.monthly_limit ? `${plan.monthly_limit.toString().replace(/\B(?=(\d{3})+(?!\d))/g, "'")} Tokens` : 'Unlimitiert')}</span>
                                                </div>
                                            </div>

                                            <p className="text-sm text-slate-400 mb-8 leading-relaxed min-h-[40px]">{plan.description}</p>

                                            <div className="space-y-4 mb-8 flex-1">
                                                <div className="space-y-2">
                                                    {plan.type === 'monthly' && !isFree && (
                                                        <div className="flex items-start gap-3 mb-4 text-emerald-400 font-bold border-b border-emerald-500/20 pb-2">
                                                            <span className="mt-0.5 w-4 h-4 text-center">✓</span>
                                                            <span>Alle Dienste des Free</span>
                                                        </div>
                                                    )}
                                                    {plan.type === 'yearly' && (() => {
                                                        const baseName = plan.name.replace(/jährlich/i, '').trim();
                                                        let monthlyEquivalent = plans.find(p => p.type === 'monthly' && p.name.replace(/monatlich/i, '').trim() === baseName);
                                                        if (!monthlyEquivalent && plan.name === 'XC Easy Jährlich') {
                                                            monthlyEquivalent = plans.find(p => p.name === 'XC PRO Monatlich');
                                                        }
                                                        if (monthlyEquivalent) {
                                                            return (
                                                                <div className="flex items-start gap-3 mb-4 text-emerald-400 font-bold border-b border-emerald-500/20 pb-2">
                                                                    <span className="mt-0.5 w-4 h-4 text-center">✓</span>
                                                                    <span>Alle Dienste des {monthlyEquivalent.name}</span>
                                                                </div>
                                                            );
                                                        }
                                                        if (!isFree) {
                                                            return (
                                                                <div className="flex items-start gap-3 mb-4 text-emerald-400 font-bold border-b border-emerald-500/20 pb-2">
                                                                    <span className="mt-0.5 w-4 h-4 text-center">✓</span>
                                                                    <span>Alle Dienste des Free</span>
                                                                </div>
                                                            );
                                                        }
                                                        return null;
                                                    })()}
                                                    {(() => {
                                                        let displayFeatures = plan.features;
                                                        if (plan.type === 'monthly' && !isFree) {
                                                            const freePlan = plans.find(p => Number(p.price) === 0);
                                                            if (freePlan) {
                                                                const freeBaseFeatures = (freePlan.features || []).map(f => f.split('|vid:')[0]);
                                                                displayFeatures = plan.features.filter(f => !freeBaseFeatures.includes(f.split('|vid:')[0]));
                                                            }
                                                        } else if (plan.type === 'yearly') {
                                                            const baseName = plan.name.replace(/jährlich/i, '').trim();
                                                            let monthlyEquivalent = plans.find(p => p.type === 'monthly' && p.name.replace(/monatlich/i, '').trim() === baseName);
                                                            if (!monthlyEquivalent && plan.name === 'XC Easy Jährlich') {
                                                                monthlyEquivalent = plans.find(p => p.name === 'XC PRO Monatlich');
                                                            }
                                                            if (monthlyEquivalent) {
                                                                const monthlyBaseFeatures = (monthlyEquivalent.features || []).map(f => f.split('|vid:')[0]);
                                                                displayFeatures = plan.features.filter(f => !monthlyBaseFeatures.includes(f.split('|vid:')[0]));
                                                            } else {
                                                                // Paid yearly plan without monthly counterpart inherits from FREE
                                                                const freePlan = plans.find(p => Number(p.price) === 0);
                                                                if (freePlan) {
                                                                    const freeBaseFeatures = (freePlan.features || []).map(f => f.split('|vid:')[0]);
                                                                    displayFeatures = plan.features.filter(f => !freeBaseFeatures.includes(f.split('|vid:')[0]));
                                                                }
                                                            }
                                                        }
                                                        return displayFeatures.map((f, i) => {
                                                            const baseFeature = f.split('|vid:')[0];
                                                            const videoId = f.split('|vid:')[1];
                                                            const linkedVideo = videoId ? tutorials.find(t => t.id === videoId) : null;
                                                            return (
                                                                <div key={i} className="flex items-start gap-3">
                                                                    <div className={`mt-1 w-4 h-4 shrink-0 rounded-full flex items-center justify-center text-[10px] ${f.includes('map::') ? 'bg-sky-500/20 text-sky-400' : 'bg-emerald-500/20 text-emerald-400'}`}>✓</div>
                                                                    <span className="text-sm text-slate-300 flex-1">{baseFeature.replace('map::', '')}</span>
                                                                    {linkedVideo && (
                                                                        <button 
                                                                            onClick={() => setPlayingVideo(linkedVideo)}
                                                                            className="ml-auto flex items-center gap-1.5 text-sky-500 hover:text-sky-400 bg-sky-500/10 hover:bg-sky-500/20 px-2 py-1 rounded-md transition-colors shrink-0 border border-sky-500/20"
                                                                            title="Tutorial Video ansehen"
                                                                        >
                                                                            <span className="text-[10px] font-bold uppercase tracking-wider">Tutorial</span>
                                                                            <Play className="w-3 h-3" fill="currentColor" />
                                                                        </button>
                                                                    )}
                                                                </div>
                                                            );
                                                        });
                                                    })()}
                                                </div>
                                            </div>

                                            <button onClick={() => onEnterApp(isFree ? 'login' : 'buy')} className={`w-full py-4 rounded-xl font-bold uppercase tracking-widest text-xs transition-all ${isRecommended
                                                ? 'bg-sky-600 text-white hover:bg-sky-500 shadow-lg shadow-sky-900/50 active:scale-95'
                                                : 'bg-slate-800 text-white hover:bg-slate-700 border border-white/5'
                                                }`}>
                                                {isFree ? 'Jetzt Starten' : 'Jetzt Abo lösen'}
                                            </button>
                                        </div>
                                    </div>
                                );
                            })
                        )}
                    </div>

                </div>
            </section>
                        </>
                    )}
                    {sliceId === 'about' && (
                        <>
{/* About Us Section */}
            <section id="ueber-uns" className={`py-24 relative border-t border-white/5 ${content.sliceBackgrounds?.about?.bgColor || content.sliceBackgrounds?.about?.bgUrl ? 'bg-transparent' : 'bg-slate-950'}`}>
                <div className="container mx-auto px-6 grid grid-cols-1 md:grid-cols-2 gap-16 items-center relative z-10">
                    <div>
                        <div className="inline-block bg-emerald-900/30 border border-emerald-500/30 rounded-full px-4 py-1.5 text-xs font-bold text-emerald-400 uppercase tracking-widest mb-6">
                            <EditableText value={content.team.subtitle} onSave={v => {
                                const newT = { ...content.team, subtitle: v };
                                handleContentUpdate({ ...content, team: newT });
                                if (editable) window.dispatchEvent(new CustomEvent('landing-page-update', { detail: { ...content, team: newT } }));
                            }} mode={editMode} />
                        </div>
                        <h2 className="text-3xl md:text-4xl font-black text-white uppercase tracking-tight mb-6">
                            <EditableText value={content.team.title} onSave={v => {
                                const newT = { ...content.team, title: v };
                                handleContentUpdate({ ...content, team: newT });
                                if (editable) window.dispatchEvent(new CustomEvent('landing-page-update', { detail: { ...content, team: newT } }));
                            }} mode={editMode} multiline className="whitespace-pre-line" />
                        </h2>
                        <div className="space-y-6 text-slate-400 leading-relaxed">
                            {content.team.text.map((txt, i) => (
                                <p key={i}>
                                    <EditableText value={txt} onSave={v => {
                                        const newText = [...content.team.text];
                                        newText[i] = v;
                                        const newT = { ...content.team, text: newText };
                                        handleContentUpdate({ ...content, team: newT });
                                        if (editable) window.dispatchEvent(new CustomEvent('landing-page-update', { detail: { ...content, team: newT } }));
                                    }} mode={editMode} multiline />
                                </p>
                            ))}
                        </div>
                    </div>
                    <div className="relative">
                        <div className="absolute inset-0 bg-gradient-to-tr from-sky-500/20 to-emerald-500/20 rounded-3xl blur-2xl transform rotate-3"></div>
                        <div className="relative bg-slate-900 border border-white/10 rounded-3xl p-2 shadow-2xl flex items-center justify-center overflow-hidden">
                            {teamPhotoUrl && (
                                <div className="w-full h-[400px] rounded-2xl overflow-hidden relative">
                                    <EditableImage 
                                        src={teamPhotoUrl} 
                                        alt="Adrian - Gründer" 
                                        editable={editable}
                                        initialScale={teamPhotoScale}
                                        initialPos={teamPhotoPos}
                                        onSave={(scale, pos) => handleSaveAssetConfig('teamPhotoUrl', scale, pos)}
                                        className="w-full h-full relative"
                                        imageClassName="w-full h-full object-cover opacity-90 hover:opacity-100 transition-opacity duration-700"
                                    />
                                </div>
                            )}
                            <div className="absolute top-4 left-4 right-4 bg-black/60 backdrop-blur-md p-3 rounded-xl border border-white/10 pointer-events-none">
                                <div className="text-white font-bold text-sm">Adrian Allenbach</div>
                                <div className="text-xs text-slate-400">Gründer & XC-Pilot</div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
                        </>
                    )}
                </div>
            ))}
            
            {playingVideo && (
                <VideoPlayerModal video={playingVideo} onClose={() => setPlayingVideo(null)} />
            )}

            <MediaGalleryModal
                isOpen={mediaModalOpen}
                onClose={() => setMediaModalOpen(false)}
                onSelect={handleMediaSelect}
            />
            {/* Sales Chatbot Widget (Floating) */}
            <SalesChat />

            {/* Footer / Legal */}
            <footer className="bg-slate-950 pt-20 pb-10 border-t border-white/5">
                <div className="container mx-auto px-6 grid grid-cols-1 md:grid-cols-4 gap-12 mb-16">
                    <div className="col-span-1 md:col-span-2">
                        <h3 className="text-2xl font-black text-white uppercase tracking-tighter mb-6">
                            <EditableText
                                value={content.footer?.title || 'Streckenfliegen.app'}
                                onSave={v => {
                                    const newContent = { ...content };
                                    if (!newContent.footer) newContent.footer = { title: '', description: '' };
                                    newContent.footer.title = v;
                                    handleContentUpdate(newContent);
                                    if (editable) window.dispatchEvent(new CustomEvent('landing-page-update', { detail: newContent }));
                                }}
                                mode={editMode}
                            />
                        </h3>
                        <div className="text-slate-500 text-sm max-w-sm">
                            <EditableText
                                value={content.footer?.description || 'Die Nr. 1 Plattform für Streckenpiloten in der Schweiz, Deutschland und Österreich.'}
                                onSave={v => {
                                    const newContent = { ...content };
                                    if (!newContent.footer) newContent.footer = { title: 'Streckenfliegen.app', description: '' };
                                    newContent.footer.description = v;
                                    handleContentUpdate(newContent);
                                    if (editable) window.dispatchEvent(new CustomEvent('landing-page-update', { detail: newContent }));
                                }}
                                mode={editMode}
                                multiline
                                tag="p"
                            />
                        </div>
                    </div>
                    <div>
                        <h4 className="text-white font-bold uppercase tracking-widest text-xs mb-6">Rechtliches</h4>
                        <ul className="space-y-3 text-sm text-slate-500 cursor-pointer">
                            <li><span onClick={() => window.dispatchEvent(new CustomEvent('show-legal-modal', { detail: 'impressum' }))} className="hover:text-sky-500 transition-colors">Rechtliches & Datenschutz</span></li>
                        </ul>
                    </div>
                    <div id="kontakt">
                        <h4 className="text-white font-bold uppercase tracking-widest text-xs mb-6">Kontakt</h4>
                        <ul className="space-y-3 text-sm text-slate-500">
                            <li>support@streckenfliegen.app</li>
                            <li>Uetendorf, Schweiz</li>
                        </ul>
                    </div>
                </div>
                <div className="text-center text-[10px] text-slate-700 font-bold uppercase tracking-widest">
                    © {new Date().getFullYear()} Streckenfliegen.app. All rights reserved.
                </div>
            </footer>

            {/* Legal Modals */}
            {activeLegal && (
                <div className="fixed inset-0 z-[1000] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setActiveLegal(null)}>
                    <div className="bg-slate-900 border border-white/10 rounded-2xl max-w-2xl w-full max-h-[80vh] overflow-y-auto p-8 shadow-2xl relative animate-in fade-in zoom-in-95 duration-200" onClick={e => e.stopPropagation()}>
                        <button onClick={() => setActiveLegal(null)} className="absolute top-4 right-4 text-primary-400 font-bold hover:text-white">✕ Schliessen</button>

                        {activeLegal === 'impressum' && (
                            <div className="space-y-4">
                                <h2 className="text-2xl font-black text-white uppercase mb-4">Impressum</h2>
                                <EditableText 
                                    value={content.legal?.impressum || `**Angaben gemäß § 5 TMG**\nStreckenfliegen.app\nRoth AG\nSchweiz\n\n**Kontakt**\nE-Mail: support@streckenfliegen.app`}
                                    onSave={v => {
                                        const newLegal = { ...(content.legal || {}), impressum: v };
                                        const newContent = { ...content, legal: newLegal };
                                        handleContentUpdate(newContent);
                                        if (editable) window.dispatchEvent(new CustomEvent('landing-page-update', { detail: newContent }));
                                    }} 
                                    mode={editMode} 
                                    multiline 
                                    className="text-slate-300 whitespace-pre-wrap" 
                                />
                            </div>
                        )}

                        {activeLegal === 'privacy' && (
                            <div className="space-y-4 text-slate-300 text-sm leading-relaxed">
                                <h2 className="text-2xl font-black text-white uppercase mb-4">Datenschutzvereinbarungen</h2>
                                <EditableText 
                                    value={content.legal?.privacy || `**Datenschutzvereinbarung (Privacy Policy)**

Wir nehmen den Schutz deiner persönlichen Daten sehr ernst. Wir behandeln deine personenbezogenen Daten vertraulich und entsprechend der gesetzlichen Datenschutzvorschriften der Schweiz (neues DSG) sowie der Europäischen Datenschutz-Grundverordnung (DSGVO). Mit dem Abschluss eines Abonnements oder der Nutzung der Plattform erklärst du dich mit dieser Datenschutzerklärung vollumfänglich einverstanden.

**1. Verantwortliche Stelle & Geltungsbereich**
Verantwortlich für die Datenverarbeitung auf dieser Website ist die Roth AG, Schweiz. Diese Erklärung gilt für alle Dienste, Subdomains und Applikationen unter Streckenfliegen.app.

**2. Erhebung, Verarbeitung und Speicherung von Daten**
Wir erheben und verarbeiten nur Daten, die für den Betrieb der Plattform, zur Erfüllung des Abonnements und zur Bereitstellung unserer Services notwendig sind:
- **Bestandsdaten:** Name, E-Mail-Adresse und Abonnement-Status zur Identifikation und Lizenzierung.
- **Flugdaten & Telemetrie:** Hochgeladene IGC-Dateien, Wegpunkte, Tracks und Thermik-Daten.
- **Nutzungsdaten:** IP-Adresse, Login-Zeitpunkte und Session-Cookies (JWT).
Diese Daten werden auf hochsicheren europäischen Servern via Microsoft Azure gehostet. Deine Profilinformationen liegen in verschlüsselten PostgreSQL-Datenbanken innerhalb der EU/CH.

**3. Analyse und Weiterverarbeitung von Flugdaten (IGC & Tracks)**
Von Nutzern hochgeladene IGC-Dateien und weitere Daten werden von unserem internen System ausgewertet. Aus diesen Daten berechnen wir globale, **vollständig anonymisierte Heatmaps und Thermik-Statistiken**, die der Community helfen. Zudem werden alle Daten, die der User hochlädt, zu Analysezwecken, zur stetigen Verbesserung des Systems sowie für das Training von Künstlicher Intelligenz (KI) genutzt. Durch das Hochladen eines Tracks stimmst du dieser Auswertung und Nutzung ausdrücklich zu. Ein Rückschluss auf deine Person ist in den aggregierten Heatmaps und KI-Modellen technisch ausgeschlossen.

**4. Einsatz von Künstlicher Intelligenz (KI)**
Unsere Analysetools greifen teilweise zur Interpretation auf künstliche Intelligenz zu. Wir übergeben **ausschliesslich technische und umgebungsrelevante Telemetriedaten** (z.B. Wetter, Höhenprofile, entpersonalisierte Thermikdaten) an das KI-Modell. Sensible persönliche Stammdaten (wie Name oder E-Mail) werden von uns **niemals** an externe KI-Provider exportiert oder zum Training fremder Modelle freigegeben.

**5. Zahlungsabwicklungen (Payment Processing)**
Alle Transaktionen und Abonnements werden durch unseren lizenzierten Schweizer Zahlungspartner **Payrexx** verarbeitet. Streckenfliegen.app selbst verarbeitet und speichert **zu keinem Zeitpunkt Kreditkarteninformationen**. Bei einer Zahlung werden lediglich Referenzdaten (Token-Identifikation, Rechnungsbetrag, E-Mail) verschlüsselt an Payrexx übertragen. Für die Zahlungsabwicklung gelten zusätzlich die Datenschutzbestimmungen von Payrexx.

**6. Datenweitergabe an Dritte & Externe APIs**
Um bestimmte Dienste bereitzustellen (Wetterdaten, Karten, Webcams), nutzen wir APIs von Drittanbietern (z.B. Windy.com, Swisstopo, Mapbox). Bei der Abfrage dieser Dienste können technische Daten (wie die IP-Adresse) an diese Server übertragen werden. Es werden jedoch keine personenbezogenen Nutzerprofile an diese Dienstleister übermittelt.

**7. Haftungsausschluss für externe Daten**
Streckenfliegen.app übernimmt keine Haftung für Datenlecks, Server-Ausfälle oder Datenschutzverletzungen, die ausserhalb unseres direkten Einflussbereichs liegen (z.B. bei Zahlungsanbietern, Cloud-Providern oder durch illegale Hacker-Angriffe). Die Nutzung der Plattform erfolgt auf eigenes Risiko.

**8. Cookies & Tracking**
Die Plattform verwendet ausschliesslich technisch notwendige (essenzielle) Cookies, wie z.B. JWT-Tokens für den Erhalt deiner Anmeldungsebene (Log-in-Session). Wir betreiben **kein clientseitiges Ad-Tracking**, keine Werbeprofile und kein Pixel-Advertising durch Dritte.

**9. Rechte der Nutzer (Auskunft, Löschung, Widerruf)**
Du hast jederzeit das Recht:
- Auskunft über deine gespeicherten Daten zu erhalten.
- Eine Berichtigung, Sperrung oder vollständige Löschung deiner personenbezogenen Daten zu verlangen.
- Deine Einwilligung zur Datenverarbeitung für die Zukunft zu widerrufen.
Bitte beachte, dass eine Löschung der Daten zur sofortigen Beendigung deines aktiven Abonnements ohne Rückerstattungsanspruch führen kann. Für Löschanfragen kontaktiere uns bitte unter support@streckenfliegen.app.

**10. Änderungen dieser Datenschutzerklärung**
Wir behalten uns vor, diese Datenschutzerklärung gelegentlich anzupassen, damit sie stets den aktuellen rechtlichen Anforderungen entspricht oder um Änderungen unserer Leistungen umzusetzen.`}
                                    onSave={v => {
                                        const newLegal = { ...(content.legal || {}), privacy: v };
                                        const newContent = { ...content, legal: newLegal };
                                        handleContentUpdate(newContent);
                                        if (editable) window.dispatchEvent(new CustomEvent('landing-page-update', { detail: newContent }));
                                    }} 
                                    mode={editMode} 
                                    multiline 
                                    className="text-slate-300 whitespace-pre-wrap" 
                                />
                            </div>
                        )}

                        {activeLegal === 'datasources' && (
                            <div className="space-y-4 text-slate-300 text-sm leading-relaxed">
                                <h2 className="text-2xl font-black text-white uppercase mb-4">Datenquellen</h2>
                                <EditableText 
                                    value={content.legal?.datasources || `Streckenfliegen.app bündelt unterschiedlichste Datenquellen im Alpenraum...\n(Platzhalter Text)`}
                                    onSave={v => {
                                        const newLegal = { ...(content.legal || {}), datasources: v };
                                        const newContent = { ...content, legal: newLegal };
                                        handleContentUpdate(newContent);
                                        if (editable) window.dispatchEvent(new CustomEvent('landing-page-update', { detail: newContent }));
                                    }} 
                                    mode={editMode} 
                                    multiline 
                                    className="text-slate-300 whitespace-pre-wrap" 
                                />
                            </div>
                        )}

                        {activeLegal === 'terms' && (
                            <div className="space-y-4">
                                <h2 className="text-2xl font-black text-white uppercase mb-4">Allgemeine Geschäftsbedingungen</h2>
                                <EditableText 
                                    value={content.legal?.terms || `**1. Geltungsbereich**\nFür die Nutzung dieser Webseite...\n\n**2. Vertragsgegenstand**\n...`}
                                    onSave={v => {
                                        const newLegal = { ...(content.legal || {}), terms: v };
                                        const newContent = { ...content, legal: newLegal };
                                        handleContentUpdate(newContent);
                                        if (editable) window.dispatchEvent(new CustomEvent('landing-page-update', { detail: newContent }));
                                    }} 
                                    mode={editMode} 
                                    multiline 
                                    className="text-slate-300 whitespace-pre-wrap" 
                                />
                            </div>
                        )}
                    </div>
                </div>
            )}

        </div>
    );
};

export default LandingPage;
